import { Dashboard } from '@/common/dashboard/Dashboard';
import { FilterContainer } from '@/common/dashboard/FilterContainer';
import { DimRearrangeButton } from '@/common/entityFramework/entityPerspective/support/DimRearrangeButton';
import { DropdownSelect } from '@/common/form/formFields/DropdownSelect';
import { EntitySelect } from '@/common/form/formFields/EntitySelect';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { McdInvTrendCard } from './McdInvTrendCard';

const metricOptions = [
  { value: 'INV', displayName: 'Invoiced Amount' },
  { value: 'DISP', displayName: 'Disputed Amount' },
  { value: 'PYMT', displayName: 'Payment Amount' },
  { value: 'DUE', displayName: 'Due Amount' },
];

// All Entity Filter Field Definitions
const entityFilterFieldDefs = [
  {
    field: 'rbtPeriodExpr',
    displayName: 'Rebate Periods',
    entity: 'PERIOD',
    code: 'RBT_PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'rbtPeriodId',
    hidden: true,
  },
  {
    field: 'paidPeriodExpr',
    displayName: 'Paid Periods',
    entity: 'PERIOD',
    code: 'PAID_PERIOD',
    module: 'sc',
    multiple: true,
    hidden: true,
  },
  {
    field: 'earnedPeriodExpr',
    displayName: 'Earned Periods',
    entity: 'PERIOD',
    code: 'EARNED_PERIOD',
    module: 'sc',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'earnedPeriodId',
  },
  {
    field: 'prodExpr',
    displayName: 'Products',
    entity: 'NDC11_PROD',
    code: 'NDC11_PROD',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'prodId',
  },
  {
    field: 'stateExpr',
    displayName: 'States',
    entity: 'STATE',
    code: 'STATE',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV_LINE',
    parentFilterFkField: 'stateId',
  },
  {
    field: 'ctrtExpr',
    displayName: 'Contracts',
    entity: 'CTRT',
    code: 'CTRT',
    module: 'md',
    multiple: true,
    parentFilterEntityModule: 'sc',
    parentFilterEntity: 'MCD_INV',
    parentFilterFkField: 'ctrtId',
  },
];

// All filters
const codeToFilter = Object.fromEntries(
  entityFilterFieldDefs.map((fieldDef) => [fieldDef.code, { displayName: fieldDef.displayName }]),
);

// Default filters
const defaultVisibleFilters = entityFilterFieldDefs
  .filter((fieldDef) => !fieldDef.hidden)
  .map((fieldDef) => fieldDef.code);

export const McdInvFinDashboard = () => {
  const rsrc = useRsrc();

  const defaultFormValues = useRef();
  const [paidPeriodIds, setPaidPeriodIds] = useState(false);
  const [visibleFilters, setVisibleFilters] = useNavStateSync('visibleFilters', defaultVisibleFilters);

  useEffect(() => {
    rsrc.data('sc', 'MCD_INV_DASHBOARD_FILTERS_DATA_PLUGIN', {}).then((res) => {
      defaultFormValues.current = {
        metric: ['INV', 'DUE'],
        rbtPeriodExpr: null,
        ctrtExpr: null,
        prodExpr: null,
        earnedPeriodExpr: ExprUtils.whereParams('id', res.earnedPeriodIds),
        earnedPeriodExprSelectedCount: res.earnedPeriodIds.length,
        earnedPeriodExprSelectedIds: res.earnedPeriodIds,
        earnedPeriodExprHeaderChecked: false,
        stateExpr: null,
        xAxis: 'EARNED_PERIOD',
        paidPeriodExpr: null,
      };
      setPaidPeriodIds(res.paidPeriodIds);
    });
  }, []);

  // Paid Periods can only be restricted once the filter options have loaded
  const fieldDefs = useMemo(
    () =>
      entityFilterFieldDefs.map((fieldDef) =>
        fieldDef.code === 'PAID_PERIOD' ? { ...fieldDef, expr: ExprUtils.whereParams('id', paidPeriodIds) } : fieldDef,
      ),
    [paidPeriodIds],
  );

  // X-axis options for the dashboard charts
  const xAxisOptions = useMemo(
    () => fieldDefs.map((fieldDef) => ({ code: fieldDef.code, displayName: fieldDef.displayName, fieldDef })),
    [fieldDefs],
  );

  // Saved nav state may reference filters that no longer exist
  const activeFieldDefs = useMemo(
    () => visibleFilters.map((code) => fieldDefs.find((fieldDef) => fieldDef.code === code)).filter(Boolean),
    [visibleFilters, fieldDefs],
  );

  const resetDims = () => {
    setVisibleFilters(defaultVisibleFilters);
  };

  if (!paidPeriodIds) return null;

  const augButtons = (buttons) => {
    buttons.unshift(
      <DimRearrangeButton
        key="filterRearrangeButton"
        setDims={setVisibleFilters}
        codeToDim={codeToFilter}
        activeDims={activeFieldDefs.map(({ code }) => ({ code }))}
      />,
    );
  };

  return (
    <Dashboard
      title="Medicaid Invoices Finance Dashboard"
      titleIcon={fas.faFileInvoice}
      entity="MCD_INV"
      defaultFormValues={defaultFormValues.current}
      augButtons={augButtons}
      onReset={resetDims}
    >
      <FilterContainer>
        <DropdownSelect
          fieldDef={{
            field: 'metric',
            displayName: 'Metric',
            multiSelect: true,
            values: metricOptions,
          }}
        />
        {activeFieldDefs.map((fieldDef) => (
          <EntitySelect key={fieldDef.field} fieldDef={{ ...fieldDef, variant: 'outlined' }} />
        ))}
      </FilterContainer>
      <SpacerLayout>
        <Spacer weights={[2, 1]}>
          <McdInvTrendCard xAxisOptions={xAxisOptions} metricOptions={metricOptions} />
        </Spacer>
      </SpacerLayout>
    </Dashboard>
  );
};
